import { Outlet, Link, useLocation } from 'react-router-dom';
import { Search, UserPlus, Shield, Phone, Mail, MapPin, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tradie-directory/artifacts/asrru0lk_Dark_png%20%281%29.png";

const Navbar = () => {
  const location = useLocation();
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <header className="sticky top-0 z-50 glass border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - links to main Healthy Homes website */}
          <a
            href="https://www.healthyhomesnz.co.nz"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
            data-testid="logo-link"
            aria-label="Visit Healthy Homes NZ"
          >
            <img 
              src={LOGO_URL} 
              alt="Healthy Homes NZ" 
              className="h-10 w-auto"
            />
          </a>
          
          {/* Navigation */}
          <nav className="flex items-center gap-2">
            <Link to="/directory">
              <Button 
                variant={isActive('/directory') ? 'default' : 'ghost'} 
                size="sm"
                className="gap-2"
                data-testid="nav-directory"
              >
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline">Find Tradies</span>
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                variant={isActive('/register') ? 'default' : 'outline'} 
                size="sm"
                className="gap-2"
                data-testid="nav-register"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">List Your Business</span>
              </Button>
            </Link>
            <Link to="/tradie/login">
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2 text-muted-foreground"
                data-testid="nav-tradie-portal"
              >
                <User className="w-4 h-4" />
                <span className="hidden md:inline">Tradie Portal</span>
              </Button>
            </Link>
            <Link to="/admin/login">
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2 text-muted-foreground"
                data-testid="nav-admin"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-stone-900 text-stone-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="mb-4">
              <img 
                src={LOGO_URL} 
                alt="Healthy Homes NZ" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-sm text-stone-400 max-w-md">
              Connecting New Zealand homeowners with qualified tradies for healthy homes compliance and maintenance services.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/directory" className="hover:text-primary transition-colors">Find Tradies</Link></li>
              <li><Link to="/register" className="hover:text-primary transition-colors">List Your Business</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><a href="https://healthyhomesnz.co.nz" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Healthy Homes NZ</a></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" />
                <span>0800 34 34 33</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <span>info@healthyhomesnz.co.nz</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5" />
                <span>Auckland, New Zealand</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-stone-800 mt-8 pt-8 text-sm text-stone-500 text-center">
          <p>&copy; {new Date().getFullYear()} Healthy Homes NZ. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
